/**
 * Read-path state machine.
 *
 * Every public read goes through this hook, so every view gets the same states
 * from Design Bible 13 without re-deriving them: loading, ready (from which a
 * view derives empty), retrying, offline-with-last-loaded-data, not-found,
 * rejected, malformed, and failed-after-the-schedule-ran-out.
 *
 * `initial` exists because this app is server-rendered. The document a crawler
 * or a first-time visitor receives already contains the catalog, so hydration
 * must not throw that away and flash a skeleton at a page that is already
 * painted. When the server preloaded a value the hook starts `ready`.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { MAX_READ_ATTEMPTS, backoffDelayMs, hasAttemptsLeft } from "./retry.js";
import type { ContractResult } from "./publicContract.js";
import { type Outcome, type Transport, correlationID, interpret, isRetryable } from "./transport.js";

export type ResourcePhase =
  | { readonly phase: "loading"; readonly attempt: number }
  | { readonly phase: "ready"; readonly loadedAt: number }
  | {
      readonly phase: "retrying";
      readonly attempt: number;
      readonly maxAttempts: number;
      readonly nextAttemptAt: number;
      readonly cause: Outcome<never>;
    }
  | { readonly phase: "failed"; readonly outcome: Outcome<never>; readonly attempts: number };

export interface Resource<T> {
  readonly state: ResourcePhase;
  /** The last successfully loaded value, kept so an offline view can show it. */
  readonly value: T | null;
  readonly loadedAt: number | null;
  readonly correlationID: string;
  readonly reload: () => void;
  readonly retryNow: () => void;
}

export interface ResourceRequest<T> {
  readonly path: string;
  /** Must be a stable reference; every validator in `publicContract.ts` is one. */
  readonly parse: (value: unknown) => ContractResult<T>;
  readonly requirement: string;
  readonly enabled?: boolean;
  /** Server-rendered value for this exact path, if the document carried one. */
  readonly initial?: T | null;
}

export function useResource<T>(transport: Transport, request: ResourceRequest<T>): Resource<T> {
  const { path, parse, requirement } = request;
  const enabled = request.enabled ?? true;
  const initial = request.initial ?? null;

  const identity = useMemo(
    () => ({ transport, path, parse, requirement, enabled }),
    [transport, path, parse, requirement, enabled]
  );
  const [snapshot, setSnapshot] = useState<{
    identity: typeof identity;
    bootstrap: boolean;
    state: ResourcePhase;
    value: T | null;
    loadedAt: number | null;
    correlation: string;
  }>(() => ({
    identity,
    bootstrap: initial !== null && enabled,
    state: initial === null ? { phase: "loading", attempt: 1 } : { phase: "ready", loadedAt: 0 },
    value: initial,
    loadedAt: initial === null ? null : 0,
    correlation: ""
  }));
  const [nonce, setNonce] = useState(0);
  // Reset before children can render another request's value, phase or time.
  // Only the mounting identity can consume the document's bootstrap value.
  const currentSnapshot = snapshot.identity === identity ? snapshot : {
    identity,
    bootstrap: false,
    state: { phase: "loading", attempt: 1 } as const,
    value: null,
    loadedAt: null,
    correlation: ""
  };
  if (snapshot !== currentSnapshot) setSnapshot(currentSnapshot);
  const bootstrap = currentSnapshot.bootstrap;

  useEffect(() => {
    if (!enabled) return;
    if (bootstrap) return;

    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    const publish = (next: Partial<Omit<typeof snapshot, "identity">>): void => {
      setSnapshot((current) => current.identity === identity ? { ...current, ...next } : current);
    };

    const run = (attempt: number): void => {
      // A fresh correlation id per attempt: each attempt is its own request in
      // core's logs, and pinning one id across retries would merge them.
      const attemptCorrelation = correlationID();
      publish({ correlation: attemptCorrelation, state: { phase: "loading", attempt } });
      void transport({
        path,
        method: "GET",
        correlationID: attemptCorrelation,
        signal: controller.signal
      }).then((reply) => {
        if (cancelled) return;
        const outcome = interpret(reply, parse, requirement);
        if (outcome.kind === "ok") {
          const at = Date.now();
          publish({ value: outcome.value, loadedAt: at, state: { phase: "ready", loadedAt: at } });
          return;
        }
        const cause = outcome as Outcome<never>;
        if (isRetryable(outcome) && hasAttemptsLeft(attempt)) {
          const delay = backoffDelayMs(attempt);
          publish({ state: {
            phase: "retrying",
            attempt,
            maxAttempts: MAX_READ_ATTEMPTS,
            nextAttemptAt: Date.now() + delay,
            cause
          } });
          timer = setTimeout(() => {
            run(attempt + 1);
          }, delay);
          return;
        }
        publish({ state: { phase: "failed", outcome: cause, attempts: attempt } });
      });
    };

    run(1);

    return () => {
      cancelled = true;
      controller.abort();
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [transport, path, parse, requirement, enabled, nonce, bootstrap, identity]);

  const reload = useCallback(() => {
    setSnapshot((current) => ({ ...current, bootstrap: false }));
    setNonce((current) => current + 1);
  }, []);

  return {
    state: currentSnapshot.state,
    value: currentSnapshot.value,
    loadedAt: currentSnapshot.loadedAt,
    correlationID: currentSnapshot.correlation,
    reload,
    retryNow: reload
  };
}
